 /* ==========================================================================
   OYEGIFTS ADD-ON POPUP V9 — DIRECT CART FLOW
   - No login step after add-ons
   - Continue/Skip adds main product + delivery details + selected add-ons
   - Redirects directly to /cart after successful cart write
============================================================================ */

(() => {
  'use strict';

  if (window.OG_ADDON_POPUP_V9_LOADED) return;
  window.OG_ADDON_POPUP_V9_LOADED = true;

  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const normalize = value => String(value || '').replace(/\s+/g, ' ').trim();
  const lower = value => normalize(value).toLowerCase();

  const DEFAULTS = {
    enabled: false,
    productFormSelector: 'form[action*="/cart/add"]',
    buyButtonSelector: 'button[name="add"], button[type="submit"], input[type="submit"], .product-form__submit, [data-add-to-cart], [data-product-submit]',
    categories: [],
    currentProductIsCourier: false,
    requireDeliverySelection: false,
    maxProductsPerSection: 50,
    maxSelectable: 50,
    currencySymbol: '₹',
    deliveryFeeProductId: 7579687125126,
    deliveryFeeVariantByAmount: {},
    deliveryFeeFallbackByType: {
      express: 49,
      fixed: 249,
      morning: 299,
      midnight: 349,
      standard: 0,
      remote: 0,
      next: 0
    }
  };

  const readConfig = () => {
    const node = $('#OgAddonPopupConfig');
    if (!node) return DEFAULTS;

    try {
      const custom = JSON.parse(node.textContent || '{}');
      return {
        ...DEFAULTS,
        ...custom,
        deliveryFeeFallbackByType: {
          ...DEFAULTS.deliveryFeeFallbackByType,
          ...(custom.deliveryFeeFallbackByType || {})
        }
      };
    } catch (error) {
      console.warn('[OG Add-ons] Invalid configuration:', error);
      return DEFAULTS;
    }
  };

  const config = readConfig();

  const popup = $('#OgAddonPopup');
  if (!popup || !config.enabled) return;

  const els = {
    dialog: $('.og-addon__dialog', popup),
    body: $('[data-og-addon-scroll]', popup),
    tabs: $('[data-og-addon-tabs]', popup),
    sections: $('[data-og-addon-sections]', popup),
    loading: $('[data-og-addon-loading]', popup),
    empty: $('[data-og-addon-empty]', popup),
    status: $('[data-og-addon-status]', popup),
    skip: $('[data-og-addon-skip]', popup),
    continueBtn: $('[data-og-addon-continue]', popup)
  };

  const state = {
    activeForm: null,
    activeVariantId: 0,
    activeProductHandle: '',
    renderedVariantId: -1,
    renderedProductHandle: '',
    productsByCategory: new Map(),
    productByVariant: new Map(),
    cardsByVariant: new Map(),
    renderedSections: [],
    selected: new Map(),
    loaded: false,
    loadingPromise: null,
    adding: false,
    activeCategory: '',
    bodyScrollY: 0,
    selectionSaveTimer: 0,
    selectionSaveIdle: 0,
    scrollRaf: 0,
    popupHideTimer: 0,
    imageObserver: null,
    cartSnapshot: null,
    cartPrefetchPromise: null
  };

  const root = window.Shopify?.routes?.root || '/';
  const api = path => `${root.replace(/\/$/, '')}/${String(path).replace(/^\//, '')}`;
  const selectionKey = config.currentProductIsCourier
    ? 'og_addons_selected_courier_v9'
    : 'og_addons_selected_express_v9';
  const transparentPixel = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

  const escapeHtml = value => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const priceNumber = value => {
    if (value === null || value === undefined) return 0;
    const original = String(value);
    const parsed = Number(original.replace(/,/g, '').replace(/[^\d.]/g, ''));
    if (!Number.isFinite(parsed)) return 0;
    if (!original.includes('.') && Number.isInteger(parsed) && parsed >= 10000) return parsed / 100;
    return parsed;
  };

  let inrFormatter = null;
  try {
    inrFormatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    });
  } catch (error) {
    inrFormatter = null;
  }

  const money = value => {
    const amount = priceNumber(value);
    if (inrFormatter) return inrFormatter.format(amount).replace(/\.00$/, '');
    return `${config.currencySymbol}${amount.toFixed(amount % 1 ? 2 : 0)}`;
  };

  const show = element => {
    if (element) element.hidden = false;
  };

  const hide = element => {
    if (element) element.hidden = true;
  };

  const setStatus = (message = '', type = '') => {
    if (!els.status) return;
    if (!message && els.status.hidden) return;
    els.status.textContent = message;
    els.status.className = `og-addon__status${type ? ` is-${type}` : ''}`;
    els.status.hidden = !message;
  };

  const postJson = async (url, payload) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Something went wrong.');
    return data;
  };

  const fetchCart = async () => {
    const response = await fetch(api('/cart.js'), {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('Cart could not be verified.');
    return response.json();
  };

  const prefetchCart = () => {
    if (state.cartSnapshot) return Promise.resolve(state.cartSnapshot);
    if (state.cartPrefetchPromise) return state.cartPrefetchPromise;
    state.cartPrefetchPromise = fetchCart()
      .then(cart => {
        state.cartSnapshot = cart;
        return cart;
      })
      .finally(() => {
        state.cartPrefetchPromise = null;
      });
    return state.cartPrefetchPromise;
  };

  const clearCartSnapshot = () => {
    state.cartSnapshot = null;
    state.cartPrefetchPromise = null;
  };

  const getCurrentHandle = () => {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/i);
    return match ? match[1] : '';
  };

  const getVariantId = form => Number(form?.querySelector('[name="id"]')?.value || 0);

  const getQuantity = form => {
    const quantity = parseInt(form?.querySelector('[name="quantity"]')?.value || '1', 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };

  const getSellingPlan = form => normalize(form?.querySelector('[name="selling_plan"]')?.value || '');

  const readFormProperties = form => {
    const properties = {};
    if (!form) return properties;

    for (const [name, rawValue] of new FormData(form).entries()) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match || typeof rawValue !== 'string') continue;
      const value = normalize(rawValue);
      if (value) properties[match[1]] = value;
    }

    return properties;
  };

  const readValue = selectors => {
    for (const selector of selectors) {
      const node = $(selector);
      if (!node) continue;
      const value = normalize(node.value || node.dataset?.value || node.textContent || '');
      if (value) return value;
    }
    return '';
  };

  const summaryText = () => $$([
    '.og-selected-delivery',
    '[data-og-selected-delivery]',
    '.og-delivery-summary',
    '.og-delivery-selected',
    '[class*="selected"][class*="delivery"]',
    '[class*="delivery"][class*="summary"]',
    '#ogDeliveryWidget',
    '.og-delivery-widget'
  ].join(',')).map(node => normalize(node.textContent)).filter(Boolean).join(' | ');

  const extractLabel = (text, label) => {
    const labels = [
      'Delivery City', 'Delivery Pincode', 'Delivery Date', 'Delivery Type', 'Delivery Slot',
      'Select Date', 'Selected Date', 'Pick Delivery Date', 'Pick Delivery Type',
      'Selected Delivery Type', 'Time Slot', 'Select Time Slot', 'Delivery Time',
      'Selected Time Slot', 'Pin Code', 'Pincode', 'Postal Code', 'Delivery Location',
      'Delivery Charge', 'Delivery Fee', 'Final Delivery Charge', 'Remote Charge'
    ];
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const others = labels
      .filter(item => item !== label)
      .map(item => item.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('|');
    const match = normalize(text).match(new RegExp(`${escaped}\\s*:?\\s*(.*?)(?=\\s*(?:${others})\\s*:?|$)`, 'i'));
    return match ? normalize(match[1]) : '';
  };

  const cleanDeliveryType = value => {
    const text = normalize(value);
    if (/morning/i.test(text)) return 'Morning Delivery';
    if (/express/i.test(text)) return 'Express Delivery';
    if (/standard/i.test(text)) return 'Standard Delivery';
    if (/fixed/i.test(text)) return 'Fixed Time Delivery';
    if (/midnight/i.test(text)) return 'Midnight Delivery';
    if (/remote/i.test(text)) return 'Remote Delivery';
    if (/next/i.test(text)) return 'Next Day Delivery';
    return text;
  };

  const cleanDeliveryDate = value => {
    let text = normalize(value)
      .replace(/^Delivery Date\s*:?\s*/i, '')
      .replace(/^Select(?:ed)? Date\s*:?\s*/i, '')
      .replace(/\bDelivery Type\b.*$/i, '')
      .replace(/\bDelivery Slot\b.*$/i, '')
      .replace(/\bDelivery Charge\b.*$/i, '')
      .trim();
    if (!text || /localStorage|function|document\.|window\.|=>|\{|\}/i.test(text)) return '';
    const direct = text.match(/\b\d{1,2}[-/]\d{1,2}[-/]\d{2,4}\b/);
    if (direct) return direct[0];
    const relative = text.match(/\b(?:Today|Tomorrow)\b(?:[\s,]+(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun))?(?:[\s,]+\d{1,2})?(?:[\s,]+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*)?/i);
    if (relative) return normalize(relative[0]);
    const monthDate = text.match(/\b\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*(?:\s*,?\s*\d{2,4})?\b/i);
    if (monthDate) return normalize(monthDate[0]);
    return text.length <= 48 ? text : '';
  };

  const cleanDeliverySlot = value => {
    const text = normalize(value)
      .replace(/^Delivery (?:Slot|Time)\s*:?\s*/i, '')
      .replace(/^Select(?:ed)? (?:Time )?Slot\s*:?\s*/i, '');
    if (!text) return '';
    const range = text.match(/\b\d{1,2}(?::\d{2})?\s*(?:AM|PM)\s*[-–]\s*\d{1,2}(?::\d{2})?\s*(?:AM|PM)\b/i);
    return normalize(range ? range[0] : text);
  };

  const selectedText = (selector, closestSelector) => {
    const selected = $(selector);
    if (!selected) return '';
    const source = selected.closest(closestSelector) || selected;
    return normalize(
      selected.value ||
      selected.dataset?.value ||
      selected.dataset?.date ||
      selected.dataset?.type ||
      selected.dataset?.slot ||
      source.dataset?.value ||
      source.textContent ||
      ''
    );
  };

  const storedDelivery = () => {
    try {
      const value = JSON.parse(localStorage.getItem('og_global_delivery_state_v1') || '{}');
      const updatedAt = Number(value.updatedAt || 0);
      if (updatedAt && Date.now() - updatedAt > 3600000) return {};
      return value && typeof value === 'object' ? value : {};
    } catch (error) {
      return {};
    }
  };

  const deliveryProperties = (form, suppliedFormProperties = null) => {
    const formProperties = suppliedFormProperties || readFormProperties(form);
    let cachedSummary = '';
    const getSummary = () => {
      if (!cachedSummary) cachedSummary = summaryText();
      return cachedSummary;
    };
    const saved = storedDelivery();
    const properties = {};

    const locationText = readValue([
      '[name="properties[Delivery Pincode]"]', '[name="properties[Pincode]"]',
      '[name="properties[Pin Code]"]', '[data-og-property-name="Delivery Pincode"]',
      '#ogLocationInput', '[data-og-location-input]', '.og-location-input',
      '.og-delivery-location input', 'input[placeholder*="pincode" i]'
    ]);
    const directLocationSource = `${formProperties['Delivery Pincode'] || ''} ${locationText} ${saved.pincode || ''}`;
    let pinMatch = directLocationSource.match(/\b\d{6}\b/);
    if (!pinMatch) {
      const summary = getSummary();
      pinMatch = `${extractLabel(summary, 'Delivery Pincode')} ${extractLabel(summary, 'Pincode')}`.match(/\b\d{6}\b/);
    }
    if (pinMatch) properties['Delivery Pincode'] = pinMatch[0];

    let city = formProperties['Delivery City'] || readValue([
      '[name="properties[Delivery City]"]', '[data-og-property-name="Delivery City"]', '[data-og-delivery-city]'
    ]) || extractLabel(getSummary(), 'Delivery City') || saved.city || '';
    if (!city && locationText) {
      city = normalize(locationText)
        .replace(/\b\d{6}\b/g, '')
        .replace(/\bIND\b/gi, '')
        .replace(/[,|\s-]+/g, ' ')
        .trim();
    }
    if (city) properties['Delivery City'] = normalize(city).replace(/\b\d{6}\b/g, '').replace(/^[,|\s-]+|[,|\s-]+$/g, '');

    const dateRaw = formProperties['Delivery Date'] || formProperties['Select Date'] || formProperties['Selected Date'] ||
      readValue([
        '[name="properties[Delivery Date]"]', '[name="properties[Select Date]"]',
        '[name="properties[Selected Date]"]', '[name="properties[Pick Delivery Date]"]',
        '[data-og-property-name="Delivery Date"]', '[data-og-delivery-date]'
      ]) || extractLabel(getSummary(), 'Delivery Date') || extractLabel(getSummary(), 'Select Date') ||
      selectedText(
        '.og-date-card.is-selected, .og-date-card.selected, .og-delivery-date .is-selected, .og-delivery-date [aria-pressed="true"], .og-delivery-date [aria-selected="true"], [class*="date"] input:checked',
        'label, .og-date-card, [class*="date"]'
      ) || saved.deliveryDate || '';
    const date = cleanDeliveryDate(dateRaw);
    if (date) properties['Delivery Date'] = date;

    const typeRaw = formProperties['Delivery Type'] || formProperties['Pick Delivery Type'] || formProperties['Selected Delivery Type'] ||
      readValue([
        '[name="properties[Delivery Type]"]', '[name="properties[Pick Delivery Type]"]',
        '[name="properties[Selected Delivery Type]"]', '[data-og-property-name="Delivery Type"]',
        '[data-og-delivery-type]'
      ]) || extractLabel(getSummary(), 'Delivery Type') ||
      selectedText(
        '.og-delivery_type-slot-grid input:checked, .og-delivery-type-slot-grid input:checked, .og-slot-card__radio:checked, input[name*="delivery_type" i]:checked, input[name*="delivery-type" i]:checked, [data-og-delivery-option].is-selected',
        'label, .og-slot-card, .delivery-option, [data-og-delivery-option], [class*="delivery"]'
      ) || saved.deliveryType || '';
    const deliveryType = cleanDeliveryType(typeRaw);
    if (deliveryType) properties['Delivery Type'] = deliveryType;

    const slotRaw = formProperties['Delivery Slot'] || formProperties['Time Slot'] || formProperties['Delivery Time'] ||
      readValue([
        '[name="properties[Delivery Slot]"]', '[name="properties[Time Slot]"]',
        '[name="properties[Delivery Time]"]', '[name="properties[Select Time Slot]"]',
        '[data-og-property-name="Delivery Slot"]', '[data-og-delivery-slot]'
      ]) || extractLabel(getSummary(), 'Delivery Slot') || extractLabel(getSummary(), 'Time Slot') ||
      selectedText(
        '#ogSlotOptions input:checked, .og-slot-options input:checked, .og-delivery-slot input:checked, input[name*="delivery_slot" i]:checked, input[name*="delivery-slot" i]:checked, [data-og-delivery-slot].is-selected',
        'label, .og-slot-card, .delivery-slot, [class*="slot"]'
      ) || saved.deliverySlot || '';
    const slot = cleanDeliverySlot(slotRaw);
    if (slot) properties['Delivery Slot'] = slot;

    return properties;
  };

  const validateDelivery = form => {
    if (!config.requireDeliverySelection || config.currentProductIsCourier) return true;
    const props = deliveryProperties(form);
    const required = ['Delivery Pincode', 'Delivery Date', 'Delivery Type', 'Delivery Slot'];
    const missing = required.filter(key => !props[key]);
    if (!missing.length) return true;

    const area = $('.og-delivery-widget, #ogDeliveryWidget, [data-og-delivery-widget]');
    area?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    window.alert(`Please select ${missing.map(key => key.replace('Delivery ', '').toLowerCase()).join(', ')} first.`);
    return false;
  };

  const validateForm = form => {
    if (!form || !getVariantId(form)) {
      window.alert('Please select a product option first.');
      return false;
    }
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
      form.reportValidity?.();
      return false;
    }
    return validateDelivery(form);
  };

  const feeFromType = type => {
    const value = lower(type);
    for (const [needle, amount] of Object.entries(config.deliveryFeeFallbackByType || {})) {
      if (value.includes(lower(needle))) return Number(amount) || 0;
    }
    return 0;
  };

  const deliveryFeeAmount = (form, props, suppliedFormProperties = null) => {
    const formProps = suppliedFormProperties || readFormProperties(form);
    const directCandidates = [
      formProps['_og_delivery_fee_amount'],
      formProps['Delivery Charge'],
      formProps['Delivery Fee'],
      readValue([
        '[name="properties[_og_delivery_fee_amount]"]',
        '[name="properties[Delivery Charge]"]',
        '[name="properties[Delivery Fee]"]',
        '[data-og-delivery-fee]',
        '[data-delivery-fee]'
      ])
    ];

    for (const candidate of directCandidates) {
      const match = String(candidate || '').replace(/,/g, '').match(/(?:₹|Rs\.?|INR)?\s*(\d+(?:\.\d+)?)/i);
      if (match) return Math.max(0, Math.round(Number(match[1]) || 0));
    }

    const summary = summaryText();
    for (const candidate of [extractLabel(summary, 'Delivery Charge'), extractLabel(summary, 'Delivery Fee')]) {
      const match = String(candidate || '').replace(/,/g, '').match(/(?:₹|Rs\.?|INR)?\s*(\d+(?:\.\d+)?)/i);
      if (match) return Math.max(0, Math.round(Number(match[1]) || 0));
    }

    return feeFromType(props['Delivery Type']);
  };

  const availableVariant = product => {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    return variants.find(variant => variant.available !== false) || variants[0] || null;
  };

  const cardImageUrl = value => {
    const source = String(value || '');
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      if (url.hostname.endsWith('shopify.com') || url.hostname.endsWith('shopifycdn.com')) {
        url.searchParams.set('width', '480');
      }
      return url.href;
    } catch (error) {
      return source;
    }
  };

  const mapProduct = product => {
    const variant = availableVariant(product);
    const image = cardImageUrl(product.images?.[0]?.src || product.image?.src || product.featured_image || '');
    if (!variant?.id) return null;
    return {
      id: Number(variant.id),
      handle: String(product.handle || ''),
      title: normalize(product.title),
      price: priceNumber(variant.price ?? product.price ?? 0),
      image,
      available: variant.available !== false
    };
  };

  const fetchCategory = async category => {
    const handle = normalize(category.handle);
    if (!handle) return [];
    const limit = 50;
    const response = await fetch(api(`/collections/${encodeURIComponent(handle)}/products.json?limit=${limit}`), {
      headers: { Accept: 'application/json' },
      cache: 'default'
    });
    if (!response.ok) throw new Error(`${category.title} add-ons could not be loaded.`);
    const data = await response.json();
    return (data.products || [])
      .map(mapProduct)
      .filter(Boolean)
      .slice(0, limit);
  };

  const saveSelection = () => {
    try {
      localStorage.setItem(selectionKey, JSON.stringify(Array.from(state.selected.keys())));
    } catch (error) {
      console.warn('[OG Add-ons] Selection could not be saved:', error);
    }
  };

  const queueSelectionSave = () => {
    window.clearTimeout(state.selectionSaveTimer);
    if (state.selectionSaveIdle && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(state.selectionSaveIdle);
      state.selectionSaveIdle = 0;
    }
    state.selectionSaveTimer = window.setTimeout(() => {
      state.selectionSaveTimer = 0;
      if ('requestIdleCallback' in window) {
        state.selectionSaveIdle = window.requestIdleCallback(() => {
          state.selectionSaveIdle = 0;
          saveSelection();
        }, { timeout: 1000 });
      } else {
        saveSelection();
      }
    }, 240);
  };

  const restoreSelection = () => {
    try {
      const ids = JSON.parse(localStorage.getItem(selectionKey) || '[]');
      const allProducts = Array.from(state.productsByCategory.values()).flat();
      const byId = new Map(allProducts.map(product => [product.id, product]));
      state.selected.clear();
      (Array.isArray(ids) ? ids : []).slice(0, config.maxSelectable).forEach(id => {
        const product = byId.get(Number(id));
        if (product?.available) state.selected.set(product.id, product);
      });
    } catch (error) {
      state.selected.clear();
    }
  };

  const cardMarkup = (product, index, eagerSection) => {
    const selected = state.selected.has(product.id);
    const eager = eagerSection && index < 9;
    const imageMarkup = product.image
      ? `<img src="${eager ? escapeHtml(product.image) : transparentPixel}"${eager ? '' : ` data-og-src="${escapeHtml(product.image)}"`} alt="${escapeHtml(product.title)}" loading="${eager ? 'eager' : 'lazy'}" fetchpriority="${eager && index < 3 ? 'high' : 'low'}" decoding="async" width="220" height="220">`
      : '<span class="og-addon__image-placeholder">🎁</span>';
    return `
      <article class="og-addon__card${selected ? ' is-selected' : ''}" data-variant-id="${product.id}">
      <button class="og-addon__card-button" type="button" aria-pressed="${selected ? 'true' : 'false'}" aria-label="Add ${escapeHtml(product.title)}">
        <span class="og-addon__image">
          ${imageMarkup}
          <span class="og-addon__check" aria-hidden="true">✓</span>
        </span>
        <span class="og-addon__title">${escapeHtml(product.title)}</span>
        <strong class="og-addon__price">${money(product.price)}</strong>
        <span class="og-addon__select">${selected ? '✓ Added' : '+ Add'}</span>
      </button>
      </article>
    `;
  };

  const indexRenderedCards = () => {
    const deferredImages = [];
    $$('.og-addon__card', els.sections).forEach(card => {
      const variantId = Number(card.dataset.variantId || 0);
      if (!variantId) return;
      card._ogButton = card.firstElementChild;
      card._ogLabel = card._ogButton?.lastElementChild || null;
      const cards = state.cardsByVariant.get(variantId) || [];
      cards.push(card);
      state.cardsByVariant.set(variantId, cards);
      const image = card._ogButton?.firstElementChild?.firstElementChild;
      if (image?.dataset?.ogSrc) deferredImages.push(image);
    });
    return deferredImages;
  };

  const loadDeferredImage = image => {
    const source = image?.dataset?.ogSrc;
    if (!source) return;
    image.src = source;
    image.removeAttribute('data-og-src');
  };

  const observeDeferredImages = images => {
    state.imageObserver?.disconnect();
    state.imageObserver = null;
    if (!images.length) return;

    if (!('IntersectionObserver' in window)) {
      images.forEach(loadDeferredImage);
      return;
    }

    state.imageObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        loadDeferredImage(entry.target);
        state.imageObserver?.unobserve(entry.target);
      });
    }, {
      root: els.body,
      rootMargin: '700px 0px',
      threshold: 0.01
    });
    images.forEach(image => state.imageObserver.observe(image));
  };

  const setActiveCategory = key => {
    if (!key || state.activeCategory === key) return;
    state.activeCategory = key;
    $$('[data-og-tab-key]', popup).forEach(tab => {
      const active = tab.dataset.ogTabKey === key;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  };

  const syncTabWithScroll = () => {
    if (!els.body) return;
    const sections = state.renderedSections;
    if (!sections.length) return;
    const marker = els.body.scrollTop + 150;
    let active = sections[0].dataset.ogCategorySection;
    sections.forEach(section => {
      if (section.offsetTop <= marker) active = section.dataset.ogCategorySection;
    });
    setActiveCategory(active);
  };

  const requestTabSync = () => {
    if (state.scrollRaf) return;
    state.scrollRaf = window.requestAnimationFrame(() => {
      state.scrollRaf = 0;
      syncTabWithScroll();
    });
  };

  const render = () => {
    const tabsFragment = document.createDocumentFragment();
    const sectionsFragment = document.createDocumentFragment();
    state.productByVariant.clear();
    state.cardsByVariant.clear();
    state.renderedSections = [];
    state.activeCategory = '';
    let totalProducts = 0;

    (config.categories || []).forEach((category, categoryIndex) => {
      const products = (state.productsByCategory.get(category.key) || []).filter(product => {
        return product.id !== state.activeVariantId && product.handle !== state.activeProductHandle;
      });
      if (!products.length) return;
      totalProducts += products.length;
      products.forEach(product => state.productByVariant.set(product.id, product));

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'og-addon__tab';
      tab.dataset.ogTabKey = category.key;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', 'false');
      tab.textContent = category.title;
      tab.addEventListener('click', () => {
        const targetSection = popup.querySelector(`[data-og-category-section="${category.key}"]`);
        if (!targetSection || !els.body) return;
        setActiveCategory(category.key);
        els.body.scrollTop = Math.max(0, targetSection.offsetTop - 8);
      });
      tabsFragment.appendChild(tab);

      const section = document.createElement('section');
      section.className = 'og-addon__section';
      section.dataset.ogCategorySection = category.key;
      section.innerHTML = `
        <div class="og-addon__section-head">
          <h3>${escapeHtml(category.title)}</h3>
          <span>${products.length} options</span>
        </div>
        <div class="og-addon__grid">${products.map((product, index) => cardMarkup(product, index, categoryIndex === 0)).join('')}</div>
      `;
      sectionsFragment.appendChild(section);
    });

    els.tabs.replaceChildren(tabsFragment);
    els.sections.replaceChildren(sectionsFragment);
    state.renderedSections = $$('[data-og-category-section]', els.sections);
    observeDeferredImages(indexRenderedCards());
    state.renderedVariantId = state.activeVariantId;
    state.renderedProductHandle = state.activeProductHandle;
    els.tabs.hidden = totalProducts === 0;
    els.empty.hidden = totalProducts !== 0;
    updateFooter();
    setActiveCategory(els.tabs.querySelector('[data-og-tab-key]')?.dataset.ogTabKey || '');
  };

  const loadProducts = () => {
    if (state.loaded) return Promise.resolve();
    if (state.loadingPromise) return state.loadingPromise;
    show(els.loading);
    hide(els.empty);
    setStatus('');

    state.loadingPromise = (async () => {
      try {
        const results = await Promise.allSettled((config.categories || []).map(fetchCategory));
        results.forEach((result, index) => {
          const category = config.categories[index];
          state.productsByCategory.set(category.key, result.status === 'fulfilled' ? result.value : []);
          if (result.status === 'rejected') console.warn('[OG Add-ons]', result.reason);
        });
        state.loaded = true;
        restoreSelection();
      } catch (error) {
        setStatus(error.message || 'Add-ons could not be loaded. You can still continue.', 'error');
        show(els.empty);
      } finally {
        state.loadingPromise = null;
        hide(els.loading);
      }
    })();

    return state.loadingPromise;
  };

  const paintCard = (card, selected) => {
    const button = card._ogButton || $('.og-addon__card-button', card);
    const label = card._ogLabel || $('.og-addon__select', card);
    card.classList.toggle('is-selected', selected);
    button?.setAttribute('aria-pressed', selected ? 'true' : 'false');
    if (label) label.textContent = selected ? '✓ Added' : '+ Add';
  };

  const toggleProduct = (product, sourceCard) => {
    if (state.selected.has(product.id)) {
      state.selected.delete(product.id);
    } else {
      if (state.selected.size >= Number(config.maxSelectable || 50)) {
        setStatus(`Maximum ${config.maxSelectable} add-ons can be selected.`, 'error');
        return;
      }
      state.selected.set(product.id, product);
    }
    const selected = state.selected.has(product.id);
    const cards = state.cardsByVariant.get(product.id) || (sourceCard ? [sourceCard] : []);
    cards.forEach(card => paintCard(card, selected));
    queueSelectionSave();
    setStatus('');
    updateFooter();
  };

  const updateFooter = () => {
    if (!els.continueBtn || state.adding) return;
    const count = state.selected.size;
    els.continueBtn.textContent = count
      ? `CONTINUE WITH ${count} ADD-ON${count === 1 ? '' : 'S'}`
      : 'CONTINUE';
    if (els.skip) els.skip.hidden = count > 0;
  };

  const openPopup = async form => {
    state.activeForm = form;
    state.activeVariantId = getVariantId(form);
    state.activeProductHandle = getCurrentHandle();
    state.activeCategory = '';
    setStatus('');
    void prefetchCart().catch(() => {});

    state.bodyScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${state.bodyScrollY}px`;
    document.body.style.width = '100%';
    document.body.classList.add('og-addon-lock');

    if (popup.parentElement !== document.body) document.body.appendChild(popup);
    window.clearTimeout(state.popupHideTimer);
    popup.hidden = false;
    popup.setAttribute('aria-hidden', 'false');
    popup.classList.remove('is-open');
    void popup.offsetWidth;
    window.requestAnimationFrame(() => popup.classList.add('is-open'));
    els.body.scrollTop = 0;
    await loadProducts();
    if (
      state.loaded &&
      (state.renderedVariantId !== state.activeVariantId || state.renderedProductHandle !== state.activeProductHandle)
    ) render();
    requestAnimationFrame(() => els.dialog?.focus());
  };

  const hidePopup = () => new Promise(resolve => {
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    window.clearTimeout(state.popupHideTimer);
    state.popupHideTimer = window.setTimeout(() => {
      popup.hidden = true;
      document.body.classList.remove('og-addon-lock');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('width');
      window.scrollTo(0, state.bodyScrollY);
      resolve();
    }, 320);
  });

  /* FAST proceed close:
     Continue/Skip should not wait 320ms for the sheet animation before /cart.
     Remove the sheet synchronously for a faster hand-off. */
  const hidePopupNow = () => {
    window.clearTimeout(state.popupHideTimer);
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    popup.hidden = true;

    document.body.classList.remove('og-addon-lock');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('width');

    /* Do not use smooth scrolling here; restore position immediately. */
    window.scrollTo(0, state.bodyScrollY);
  };

  const isFeeItem = item => {
    return Number(item.product_id) === Number(config.deliveryFeeProductId) || lower(item.properties?._og_delivery_fee) === 'true';
  };

  const isAddonItem = item => lower(item.properties?._addon_item) === 'true';

  const parentType = () => config.currentProductIsCourier ? 'courier' : 'express';

  const feeForCartItem = item => {
    const direct = priceNumber(item.properties?._og_delivery_fee_amount || 0);
    if (Number.isFinite(direct) && direct > 0) return direct;
    return feeFromType(item.properties?.['Delivery Type'] || '');
  };

  const cartItemParentType = item => lower(
    item.properties?._og_parent_type ||
    item.properties?._addon_parent_type ||
    item.properties?.['_Product Delivery Mode'] ||
    ''
  );

  const addConfigurationToCart = async () => {
    const form = state.activeForm;
    const variantId = getVariantId(form) || state.activeVariantId;
    if (!variantId) throw new Error('Main product variant was not found.');

    const type = parentType();
    const quantity = getQuantity(form);
    const plan = getSellingPlan(form);
    const formProperties = readFormProperties(form);
    const delivery = type === 'express' ? deliveryProperties(form, formProperties) : {};
    const fee = type === 'express' ? deliveryFeeAmount(form, delivery, formProperties) : 0;
    const group = `og_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const properties = {
      ...formProperties,
      ...delivery,
      _addon_group_id: group,
      _has_addons: state.selected.size ? 'true' : 'false',
      _og_parent_type: type,
      _addon_parent_type: type,
      '_Product Delivery Mode': type
    };

    if (type === 'courier') {
      ['Delivery City', 'Delivery Pincode', 'Delivery Date', 'Delivery Type', 'Delivery Slot', 'Delivery Charge', 'Delivery Fee']
        .forEach(key => delete properties[key]);
    } else {
      [
        'Select Date', 'Selected Date', 'Pick Delivery Date', 'Pick Delivery Type',
        'Selected Delivery Type', 'Time Slot', 'Select Time Slot', 'Delivery Time',
        'Selected Time Slot', 'Pin Code', 'Pincode', 'Postal Code',
        'Delivery Location', 'Selected Location'
      ].forEach(key => delete properties[key]);
      if (fee > 0) properties._og_delivery_fee_amount = String(fee);
    }

    const parentItem = { id: variantId, quantity, properties };
    if (plan) parentItem.selling_plan = plan;

    const addonItems = Array.from(state.selected.values())
      .filter(product => product.id !== variantId)
      .map(product => ({
        id: product.id,
        quantity,
        properties: {
          _addon_item: 'true',
          _addon_group_id: group,
          _addon_for_product: normalize($('.product__title, [data-product-title], h1')?.textContent || document.title),
          _addon_parent_variant_id: String(variantId),
          _addon_parent_key: `pending_${group}`,
          _addon_parent_type: type
        }
      }));

    /* One cart read creates the complete removal/addition plan. */
    const cart = await prefetchCart();
    clearCartSnapshot();
    const cartItems = cart.items || [];
    const oldParents = cartItems.filter(item => (
      Number(item.variant_id) === variantId && !isAddonItem(item) && !isFeeItem(item)
    ));
    const oldParentKeys = new Set(oldParents.map(item => String(item.key || '')));
    const oldGroups = new Set(oldParents.map(item => normalize(item.properties?._addon_group_id)).filter(Boolean));
    const updates = {};
    const remainingItems = [];
    let removedQuantity = 0;

    cartItems.forEach(item => {
      const itemGroup = normalize(item.properties?._addon_group_id);
      const itemParentKey = normalize(item.properties?._addon_parent_key);
      const itemParentVariant = Number(item.properties?._addon_parent_variant_id || 0);
      const removeParent = oldParentKeys.has(String(item.key || ''));
      const removeAddon = isAddonItem(item) && (
        oldGroups.has(itemGroup) ||
        oldParentKeys.has(itemParentKey) ||
        itemParentVariant === variantId
      );
      const removeItem = isFeeItem(item) || removeParent || removeAddon;

      if (removeItem) {
        updates[item.key] = 0;
        removedQuantity += Number(item.quantity || 0);
      } else {
        remainingItems.push(item);
      }
    });

    const otherMaximumFee = Math.max(0, ...remainingItems
      .filter(item => !isAddonItem(item) && cartItemParentType(item).includes('express'))
      .map(feeForCartItem));
    const expectedFee = Math.max(otherMaximumFee, type === 'express' ? Number(fee || 0) : 0);
    const feeVariantId = Number(config.deliveryFeeVariantByAmount?.[String(Math.round(expectedFee))] || 0);
    if (expectedFee > 0 && !feeVariantId) {
      throw new Error(`Delivery fee configuration is missing for ₹${Math.round(expectedFee)}.`);
    }

    const itemsToAdd = [parentItem, ...addonItems];
    if (expectedFee > 0) {
      itemsToAdd.push({
        id: feeVariantId,
        quantity: 1,
        properties: {
          _og_delivery_fee: 'true',
          _og_delivery_fee_amount: String(Math.round(expectedFee)),
          _og_delivery_fee_group: group
        }
      });
    }

    /* The original cart line keys and this new group's line keys are distinct, so both mutations are safe in parallel. */
    const addRequest = postJson(api('/cart/add.js'), { items: itemsToAdd });
    const updateRequest = Object.keys(updates).length
      ? postJson(api('/cart/update.js'), { updates })
      : Promise.resolve();
    await Promise.all([addRequest, updateRequest]);
    const addedQuantity = itemsToAdd.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    return {
      ...cart,
      item_count: Math.max(0, Number(cart.item_count || 0) - removedQuantity + addedQuantity)
    };
  };

  /* --------------------------------------------------------------------------
     DIRECT CART FLOW
     Continue/Skip -> cart write -> direct /cart redirect.
     No account/login page, pending marker, SDK listener, or OTP wait.
  -------------------------------------------------------------------------- */
  const cartPageUrl = () => api('/cart');

  const prefetchCartPage = () => {
    try {
      const href = cartPageUrl();
      if (document.querySelector('link[data-og-cart-prefetch]')) return;
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = href;
      link.as = 'document';
      link.dataset.ogCartPrefetch = '1';
      document.head.appendChild(link);
    } catch (error) {}
  };

  const showCartRedirectOverlay = (message = 'Opening your cart…') => {
    let layer = document.getElementById('OgFastCartRedirect');
    if (!layer) {
      layer = document.createElement('div');
      layer.id = 'OgFastCartRedirect';
      layer.setAttribute('role', 'status');
      layer.setAttribute('aria-live', 'polite');
      layer.innerHTML = `
        <div class="og-fast-cart__box">
          <span class="og-fast-cart__spinner" aria-hidden="true"></span>
          <strong data-og-fast-cart-text>${escapeHtml(message)}</strong>
        </div>
      `;
      if (!document.getElementById('OgFastCartRedirectStyle')) {
        const style = document.createElement('style');
        style.id = 'OgFastCartRedirectStyle';
        style.textContent = `
          #OgFastCartRedirect{position:fixed;inset:0;z-index:2147483647;display:grid;place-items:center;padding:24px;background:rgba(255,255,255,.96);backdrop-filter:blur(2px);font-family:inherit;color:#15151a}
          #OgFastCartRedirect .og-fast-cart__box{display:flex;flex-direction:column;align-items:center;gap:10px;text-align:center}
          #OgFastCartRedirect .og-fast-cart__spinner{width:36px;height:36px;border-radius:50%;border:3px solid #ececec;border-top-color:#087443;animation:ogFastCartSpin .62s linear infinite}
          #OgFastCartRedirect strong{font-size:15px;line-height:1.2;font-weight:800}
          @keyframes ogFastCartSpin{to{transform:rotate(360deg)}}
        `;
        document.head.appendChild(style);
      }
      document.body.appendChild(layer);
    } else {
      const label = layer.querySelector('[data-og-fast-cart-text]');
      if (label) label.textContent = message;
      layer.hidden = false;
    }
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return layer;
  };

  const submitFinal = async () => {
    if (state.adding) return;
    state.adding = true;
    setStatus('');
    if (els.continueBtn) {
      els.continueBtn.disabled = true;
      els.continueBtn.setAttribute('aria-busy', 'true');
      els.continueBtn.classList.add('is-loading');
      els.continueBtn.textContent = 'ADDING…';
    }
    if (els.skip) els.skip.disabled = true;

    try {
      const cart = await addConfigurationToCart();
      document.dispatchEvent(new CustomEvent('og:addons:added', {
        detail: { cart, selectedAddons: state.selected.size }
      }));
      hidePopupNow();
      showCartRedirectOverlay('Opening your cart…');
      window.location.assign(cartPageUrl());
    } catch (error) {
      console.error('[OG Add-ons] Add-to-cart flow failed:', error);
      setStatus(error.message || 'Unable to add this gift. Please try again.', 'error');
      state.adding = false;
      if (els.continueBtn) {
        els.continueBtn.disabled = false;
        els.continueBtn.removeAttribute('aria-busy');
        els.continueBtn.classList.remove('is-loading');
      }
      if (els.skip) els.skip.disabled = false;
      updateFooter();
    }
  };

  const isBuyButton = button => {
    if (!button) return false;
    const text = lower(button.textContent || button.value);
    return (
      button.matches(config.buyButtonSelector) ||
      text.includes('send this gift now') ||
      text.includes('buy now') ||
      text.includes('add to cart') ||
      text.includes('send gift') ||
      text.includes('send this gift')
    );
  };

  const formFromButton = button => button.closest(config.productFormSelector) || button.closest('form') || $(config.productFormSelector);

  document.addEventListener('click', event => {
    /* Nothing inside the add-on popup may be interpreted as the main product
       Add-to-Cart/Buy button. */
    if (popup.contains(event.target)) return;

    const button = event.target.closest(config.buyButtonSelector);
    if (!button || !isBuyButton(button)) return;

    const form = formFromButton(button);
    if (!form?.matches(config.productFormSelector)) return;

    if (!validateForm(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    openPopup(form);
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form?.matches(config.productFormSelector)) return;
    if (event.submitter && !isBuyButton(event.submitter)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (validateForm(form)) openPopup(form);
  }, true);

  /* X / overlay close / Escape = close popup only.
     No add-to-cart and no redirect. */
  popup.addEventListener('click', event => {
    const close = event.target.closest('[data-og-addon-close]');
    if (!close) return;

    event.preventDefault();
    event.stopPropagation();
    void hidePopup();
  });

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape' || popup.hidden) return;
    event.preventDefault();
    void hidePopup();
  });

  /* ONLY Continue or Skip can write the cart and proceed to /cart. */
  els.continueBtn?.addEventListener('click', event => {
    event.preventDefault();
    event.stopPropagation();
    void submitFinal();
  });

  els.skip?.addEventListener('click', event => {
    event.preventDefault();
    event.stopPropagation();
    void submitFinal();
  });

  /* Add-on select/unselect = LOCAL UI ONLY.
     Selection alone never writes the cart and never redirects. */
  els.sections?.addEventListener('click', event => {
    const button = event.target.closest('.og-addon__card-button');
    if (!button || !els.sections.contains(button)) return;

    event.preventDefault();
    event.stopPropagation();

    const card = button.closest('.og-addon__card');
    const product = state.productByVariant.get(Number(card?.dataset.variantId || 0));

    if (card && product) toggleProduct(product, card);
  });

  els.body?.addEventListener('scroll', requestTabSync, { passive: true });

  window.OGAddonPopup = {
    open: form => openPopup(form || $(config.productFormSelector)),
    add: submitFinal,
    reload: async () => {
      state.loaded = false;
      state.loadingPromise = null;
      state.productsByCategory.clear();
      await loadProducts();
      render();
    }
  };

  const warmProductCache = () => {
    const start = () => { void loadProducts(); };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(start, { timeout: 3000 });
    } else {
      window.setTimeout(start, 1500);
    }
  };

  const boot = () => {
    warmProductCache();
    prefetchCartPage();
  };

  if (document.readyState === 'complete') {
    boot();
  } else {
    window.addEventListener('load', boot, { once: true });
  }
})();